import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import {
  BriefcaseIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";

const ProfessionalInfo = ({ showMessage, user }) => {
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    job_title: user?.job_title || "",
    department: user?.department || "",
    company: user?.company || "",
    bio: user?.bio || "",
    website: user?.website || "",
    linkedin_url: user?.linkedin_url || "",
    twitter_url: user?.twitter_url || "",
  });

  const MAX_BIO_LENGTH = 500;
  const bioCharCount = formData.bio.length;

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Enforce bio character limit
    if (name === "bio" && value.length > MAX_BIO_LENGTH) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty URLs are valid
    try {
      new URL(url.startsWith("http") ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const formatUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `https://${url}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate URLs
    const urlFields = ["website", "linkedin_url", "twitter_url"];
    for (const field of urlFields) {
      if (formData[field] && !validateUrl(formData[field])) {
        showMessage(
          "error",
          `Please enter a valid ${field.replace("_url", "").replace("_", " ")} URL`,
        );
        setIsLoading(false);
        return;
      }
    }

    try {
      // Format URLs before sending
      const formattedData = {
        ...formData,
        website: formData.website ? formatUrl(formData.website) : "",
        linkedin_url: formData.linkedin_url
          ? formatUrl(formData.linkedin_url)
          : "",
        twitter_url: formData.twitter_url
          ? formatUrl(formData.twitter_url)
          : "",
      };

      const result = await updateUser(formattedData);
      if (result.success) {
        showMessage(
          "success",
          "Professional information updated successfully!",
        );
      } else {
        showMessage(
          "error",
          result.error || "Failed to update professional information",
        );
      }
    } catch (error) {
      showMessage("error", "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Job Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Job Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="job_title"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              <BriefcaseIcon className="inline h-4 w-4 mr-1" />
              Job Title
            </label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., Marketing Manager"
            />
          </div>

          <div>
            <label
              htmlFor="department"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              <BuildingOfficeIcon className="inline h-4 w-4 mr-1" />
              Department
            </label>
            <input
              type="text"
              id="department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., Marketing & Communications"
            />
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="company"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Company/Organization
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., Acme Corporation"
            />
          </div>
        </div>
      </div>

      {/* Bio */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Professional Bio
        </h3>
        <div>
          <label
            htmlFor="bio"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            <DocumentTextIcon className="inline h-4 w-4 mr-1" />
            About You
          </label>
          <textarea
            id="bio"
            name="bio"
            rows={4}
            value={formData.bio}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
            placeholder="Tell us about yourself, your role, and your expertise..."
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">
              Share your professional background and what you do.
            </p>
            <span
              className={`text-xs ${
                bioCharCount > MAX_BIO_LENGTH * 0.9
                  ? "text-orange-600"
                  : bioCharCount === MAX_BIO_LENGTH
                    ? "text-red-600"
                    : "text-gray-500"
              }`}
            >
              {bioCharCount}/{MAX_BIO_LENGTH}
            </span>
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Social Links</h3>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="website"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              <LinkIcon className="inline h-4 w-4 mr-1" />
              Personal Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div>
            <label
              htmlFor="linkedin_url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              <svg
                className="inline h-4 w-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                  clipRule="evenodd"
                />
              </svg>
              LinkedIn Profile
            </label>
            <input
              type="url"
              id="linkedin_url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="https://linkedin.com/in/yourusername"
            />
          </div>

          <div>
            <label
              htmlFor="twitter_url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              <svg
                className="inline h-4 w-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
              </svg>
              Twitter/X Profile
            </label>
            <input
              type="url"
              id="twitter_url"
              name="twitter_url"
              value={formData.twitter_url}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="https://twitter.com/yourusername"
            />
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Privacy Note:</strong> Social links will only be visible to
            your team members and can be controlled through your privacy
            settings.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Saving...</span>
            </>
          ) : (
            <span>Save Changes</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default ProfessionalInfo;
